<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from "vue"
import { useHelpManualState } from "../composables/useHelpManualState"
import { groupArticlesByCategory, useHelpArticles } from "../composables/useHelpArticles"

const props = defineProps({
  manual: { type: String, required: true },
  labels: { type: Object, default: () => ({}) },
  sections: { type: Array, default: () => [] },
  articlesUrl: { type: String, default: "" },
  searchUrl: { type: String, default: "" },
  hostElement: { type: Object, default: null },
})

const offcanvasElement = ref(null)
const contentElement = ref(null)
const searchInput = ref(null)
let offcanvasInstance = null
let searchDebounceTimer = null

const { isOpen, setOpen, activeSection, setActiveSection, isPinned, setPinned } = useHelpManualState(props.manual)

const {
  articles,
  isLoading,
  loadError,
  searchQuery,
  searchResults,
  isSearching,
  searchError,
  tocGroups,
  loadArticles,
  performSearch,
  clearSearch,
} = useHelpArticles(props.articlesUrl, props.searchUrl)

const currentSectionId = ref(null)
const pinned = ref(isPinned())
const localSearchQuery = ref("")

const usesDatabase = computed(() => Boolean(props.articlesUrl))

const fallbackSections = computed(() => {
  return groupArticlesByCategory(props.sections).flatMap((group) => group.articles)
})

const allSections = computed(() => {
  if (usesDatabase.value) return articles.value
  return fallbackSections.value
})

const displayTocGroups = computed(() => {
  if (usesDatabase.value) return tocGroups.value
  return groupArticlesByCategory(props.sections)
})

const currentSection = computed(() => {
  return allSections.value.find((section) => section.id === currentSectionId.value) || allSections.value[0] || null
})

const showingSearchResults = computed(() => searchQuery.value.length > 0 && searchResults.value !== null)

function label(key, fallback = "") {
  return props.labels[key] || fallback
}

function sectionContent(section) {
  return section.content || section.content_html || ""
}

function selectSection(sectionId) {
  currentSectionId.value = sectionId
  setActiveSection(sectionId)
  clearSearch()
  localSearchQuery.value = ""
  nextTick(() => {
    contentElement.value?.scrollTo({ top: 0, behavior: "smooth" })
  })
}

function onSearchInput() {
  clearTimeout(searchDebounceTimer)
  searchDebounceTimer = setTimeout(() => {
    if (props.searchUrl) {
      performSearch(localSearchQuery.value)
      return
    }

    filterLocalSections(localSearchQuery.value)
  }, 250)
}

function filterLocalSections(query) {
  const trimmed = query.trim().toLowerCase()
  searchQuery.value = trimmed

  if (!trimmed) {
    searchResults.value = null
    return
  }

  searchResults.value = allSections.value.filter((section) => {
    const haystack = [section.title, section.summary, sectionContent(section)]
      .filter(Boolean)
      .join(" ")
      .toLowerCase()
    return haystack.includes(trimmed)
  })
}

function selectSearchResult(result) {
  const sectionId = result.id || result.slug
  if (result.content) {
    const existing = articles.value.find((s) => (s.id || s.slug) === sectionId)
    if (existing) existing.content = result.content
  }
  selectSection(sectionId)
}

function showOffcanvas(sectionId = null) {
  if (sectionId) selectSection(sectionId)
  offcanvasInstance?.show()
}

function hideOffcanvas() {
  offcanvasInstance?.hide()
}

function toggleOffcanvas() {
  if (offcanvasElement.value?.classList.contains("show")) {
    hideOffcanvas()
  } else {
    showOffcanvas()
  }
}

function togglePinned() {
  pinned.value = !pinned.value
  setPinned(pinned.value)
  applyBackdropSetting()
}

function applyBackdropSetting() {
  if (!offcanvasInstance) return
  offcanvasInstance._config.backdrop = pinned.value ? false : true
  offcanvasInstance._config.scroll = pinned.value
}

function onShown() {
  setOpen(true)
}

function onHidden() {
  setOpen(false)
}

function onOpenEvent(event) {
  const { manual, section } = event.detail || {}
  if (manual && manual !== props.manual) return
  showOffcanvas(section || null)
}

function onToggleEvent(event) {
  const { manual } = event.detail || {}
  if (manual && manual !== props.manual) return
  toggleOffcanvas()
}

function initializeSection() {
  const fallback = allSections.value[0]?.id || allSections.value[0]?.slug || null
  currentSectionId.value = activeSection(fallback)
}

onMounted(async () => {
  if (usesDatabase.value) {
    await loadArticles()
  }

  initializeSection()

  if (offcanvasElement.value && window.bootstrap?.Offcanvas) {
    offcanvasInstance = new window.bootstrap.Offcanvas(offcanvasElement.value, {
      backdrop: true,
      scroll: false,
    })

    offcanvasElement.value.addEventListener("shown.bs.offcanvas", onShown)
    offcanvasElement.value.addEventListener("hidden.bs.offcanvas", onHidden)

    applyBackdropSetting()

    if (isOpen()) {
      nextTick(() => showOffcanvas())
    }
  }

  document.addEventListener("help-manual:open", onOpenEvent)
  document.addEventListener("help-manual:toggle", onToggleEvent)
})

onUnmounted(() => {
  clearTimeout(searchDebounceTimer)
  document.removeEventListener("help-manual:open", onOpenEvent)
  document.removeEventListener("help-manual:toggle", onToggleEvent)

  offcanvasElement.value?.removeEventListener("shown.bs.offcanvas", onShown)
  offcanvasElement.value?.removeEventListener("hidden.bs.offcanvas", onHidden)
  offcanvasInstance?.dispose()
})

watch(allSections, () => {
  if (!allSections.value.some((section) => (section.id || section.slug) === currentSectionId.value)) {
    initializeSection()
  }
})
</script>

<template>
  <div class="help-manual-root">
    <div
      ref="offcanvasElement"
      class="offcanvas offcanvas-end help-manual-offcanvas"
      tabindex="-1"
      :id="`helpManualOffcanvas-${manual}`"
      :aria-labelledby="`helpManualTitle-${manual}`"
    >
      <div class="offcanvas-header help-manual-header">
        <div class="help-manual-header-text">
          <h5 :id="`helpManualTitle-${manual}`" class="offcanvas-title">
            <i class="bi bi-question-circle me-2" aria-hidden="true" />
            {{ label("title", "Help") }}
          </h5>
          <p v-if="label('subtitle')" class="help-manual-subtitle mb-0">
            {{ label("subtitle") }}
          </p>
        </div>
        <div class="help-manual-header-actions">
          <button
            type="button"
            class="btn btn-sm help-manual-pin-btn"
            :class="pinned ? 'btn-primary' : 'btn-outline-secondary'"
            data-bs-toggle="tooltip"
            :data-bs-title="pinned ? label('unpin', 'Unpin') : label('pin', 'Keep open')"
            :aria-label="pinned ? label('unpin', 'Unpin') : label('pin', 'Keep open')"
            @click="togglePinned"
          >
            <i class="bi" :class="pinned ? 'bi-pin-angle-fill' : 'bi-pin-angle'" aria-hidden="true" />
          </button>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="offcanvas"
            :aria-label="label('close', 'Close')"
          />
        </div>
      </div>

      <div v-if="usesDatabase || sections.length" class="help-manual-search-bar">
        <div class="input-group input-group-sm">
          <span class="input-group-text"><i class="bi bi-search" aria-hidden="true" /></span>
          <input
            ref="searchInput"
            v-model="localSearchQuery"
            type="search"
            class="form-control"
            :placeholder="label('search_placeholder', 'Search help...')"
            :aria-label="label('search_placeholder', 'Search help...')"
            @input="onSearchInput"
          />
          <button
            v-if="localSearchQuery"
            type="button"
            class="btn btn-outline-secondary"
            :aria-label="label('clear_search', 'Clear search')"
            @click="localSearchQuery = ''; clearSearch()"
          >
            <i class="bi bi-x" aria-hidden="true" />
          </button>
        </div>
      </div>

      <div class="offcanvas-body help-manual-body">
        <div v-if="isLoading" class="help-manual-status">
          <i class="bi bi-arrow-repeat spin" aria-hidden="true" />
          {{ label("loading", "Loading...") }}
        </div>

        <div v-else-if="loadError" class="help-manual-status text-danger">
          {{ loadError }}
        </div>

        <template v-else>
          <nav
            v-if="!showingSearchResults"
            class="help-manual-nav"
            :aria-label="label('table_of_contents', 'Table of contents')"
          >
            <div
              v-for="group in displayTocGroups"
              :key="group.category || 'default'"
              class="help-manual-nav-group"
            >
              <div v-if="group.category" class="help-manual-nav-category">
                {{ group.category }}
              </div>
              <button
                v-for="section in group.articles"
                :key="section.id || section.slug"
                type="button"
                class="help-manual-nav-item"
                :class="{ active: (section.id || section.slug) === currentSectionId }"
                @click="selectSection(section.id || section.slug)"
              >
                <i v-if="section.icon" :class="section.icon" class="me-2" aria-hidden="true" />
                {{ section.title }}
              </button>
            </div>
          </nav>

          <div v-else class="help-manual-nav help-manual-search-results">
            <div class="help-manual-nav-category">
              {{ label("search_results", "Search results") }}
            </div>
            <div v-if="isSearching" class="help-manual-status">
              {{ label("loading", "Loading...") }}
            </div>
            <div v-else-if="searchError" class="help-manual-status text-danger">
              {{ searchError }}
            </div>
            <div v-else-if="!searchResults.length" class="help-manual-status">
              {{ label("no_results", "No results found") }}
            </div>
            <button
              v-for="result in searchResults"
              :key="result.id || result.slug"
              type="button"
              class="help-manual-nav-item"
              @click="selectSearchResult(result)"
            >
              <span class="d-block fw-medium">{{ result.title }}</span>
              <small v-if="result.summary" class="text-muted">{{ result.summary }}</small>
            </button>
          </div>

          <article
            ref="contentElement"
            class="help-manual-content"
            :aria-labelledby="`helpManualTitle-${manual}`"
          >
            <h6 v-if="currentSection" class="help-manual-content-title">
              {{ currentSection.title }}
            </h6>
            <p v-if="currentSection?.summary" class="help-manual-content-summary text-muted">
              {{ currentSection.summary }}
            </p>
            <div
              v-if="currentSection"
              class="help-manual-content-body"
              v-html="sectionContent(currentSection)"
            />
          </article>
        </template>
      </div>
    </div>
  </div>
</template>
